/**
 * Checks whether the browser supports localStorage
 *
 * @returns {Boolean} Whether localStorage is supported or not
 */
export const supportsLocalStorage = () => window.localStorage !== undefined;

/**
 * Sets an item in the localStorage, converting it to JSON
 * @param {String} key The key to use
 * @param {*} value The value to store
 * @returns {void}
 */
export const setJsonItem = (key, value) => window.localStorage.setItem(key, JSON.stringify(value));

/**
 * Sets an item in the localStorage, without converting it to JSON
 * @param {String} key The key to use
 * @param {*} value The value to store
 * @returns {void}
 */
export const setItem = (key, value) => window.localStorage.setItem(key, value);

/**
 * Retrieves an item from localStorage and parses it from JSON.
 *
 * This function fetches the specified item from localStorage using the given key.
 * If the item exists, it is parsed from JSON and returned. If the item does not exist, `null` is returned.
 *
 * @param {string} key - The key corresponding to the stored item.
 * @returns {*} The parsed value if the item exists, or `null` if it does not.
 */
export const getJsonItem = (key) => {
    const item = window.localStorage.getItem(key);

    return item !== null ? JSON.parse(item) : null;
};

/**
 * Gets an item from the localStorage, without converting it from JSON
 * @param {String} key The key to use
 * @param {*} value The value to store
 * @returns {*} The stored value
 */
export const getItem = key => window.localStorage.getItem(key);

/**
 * Removes a key from the localStorage
 *
 * @param {String} key The key to remove
 * @returns {void}
 */
export const removeItem = key => window.localStorage.removeItem(key);

/**
 * Clears the localStorage
 *
 * @returns {void}
 */
export const clearAllItems = () => window.localStorage.clear();

/**
 * Checks if a given key exists in the localStorage
 *
 * @param {String} key The key to check for
 * @returns {Boolean} Whether the key exists or not
 */
export const hasItem = key => getItem(key) !== null;

/**
 * Returns the number of items in the localStorage
 *
 * @returns {Number} The number of items
 */
export const countItems = () => window.localStorage.length;

/**
 * Retrieves an item from storage and checks its expiry.
 *
 * This function attempts to fetch a JSON item from storage using the provided key.
 * It checks whether the item has expired based on the current time and the stored expiry time.
 * If the item is expired, it removes the item from storage and returns null.
 * Otherwise, it returns the item's value.
 *
 * @param {string} key - The key used to identify the item in storage.
 * @returns {*} The value of the item if it exists and hasn't expired, otherwise null.
 */
export const getItemWithExpiry = key => {
    const item = getJsonItem(key);

    if (!item) {
        return null;
    }

    const { value, expiry } = item;

    if (Date.now() > expiry) {
        removeItem(key);

        return null;
    }

    return value;
};

/**
 * Stores an item in storage with an expiry time.
 *
 * This function saves a JSON item in storage under the specified key.
 * The item will expire after a given time-to-live (TTL) in seconds.
 * The expiry time is calculated based on the current timestamp.
 *
 * @param {string} key - The key under which the item is stored.
 * @param {*} value - The value to be stored.
 * @param {number} ttl - Time-to-live in seconds before the item expires.
 * @returns {void}
 */
export const setItemWithExpiry = (key, value, ttl) => {
    const expiry = Date.now() + (Number(ttl) * 1000);

    setJsonItem(key, {
        value,
        expiry,
    });
};
